/**
 * KAN-743: каталог: категории, страница товаров, карточка товара.
 * KAN-754: ручные категории и товары: создание, изменение, удаление. После
 * любой правки обновляются все запросы каталога (дерево, списки, карточки,
 * поиск товара в заказе).
 * KAN-781: постраничный список товаров (выбор в заказ по категории).
 */
import { useMemo } from 'react';
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type Query,
} from '@tanstack/react-query';
import { catalogApi } from '@/services/api/catalog';
import { CATALOG_QUERY_KEY } from '@/services/api/queryKeys';
import { buildCategoryTree } from '@/lib/catalogTree';
import { categoryOptions, type CategoryOption } from '@/lib/catalogForm';
import { nextCatalogPage } from '@/lib/orderProductPicker';
import type {
  CatalogCategoryBody,
  CatalogId,
  CatalogProductBody,
  CatalogProductsParams,
} from '@/types/catalog';

/** 404 от API: организация не интернет-магазин или товара нет. */
export function isNotFoundError(error: unknown): boolean {
  return (error as { response?: { status?: number } } | null)?.response?.status === 404;
}

/** enabled=false: запрос не идёт (KAN-781: панель добавления товара закрыта). */
export function useCatalogCategories(enabled = true) {
  return useQuery({
    queryKey: [...CATALOG_QUERY_KEY, 'categories'],
    queryFn: catalogApi.categories,
    staleTime: 60_000,
    retry: false,
    enabled,
  });
}

/** enabled=false: запрос не идёт (KAN-746: поиск товара в заказе до ввода текста). */
export function useCatalogProducts(params: CatalogProductsParams, enabled = true) {
  return useQuery({
    queryKey: [...CATALOG_QUERY_KEY, 'products', params],
    queryFn: () => catalogApi.products(params),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    retry: false,
    enabled,
  });
}

/**
 * KAN-781: тот же список постранично: страницы накапливаются, следующая
 * запрашивается fetchNextPage, пока в ответе есть next. Смена params даёт
 * новый ключ и начинает с первой страницы; пока новая грузится, видна старая.
 */
export function useCatalogProductsPages(params: CatalogProductsParams, enabled = true) {
  return useInfiniteQuery({
    queryKey: [...CATALOG_QUERY_KEY, 'products', 'pages', params],
    queryFn: ({ pageParam }) => catalogApi.products({ ...params, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (last, pages) => nextCatalogPage(last, pages.length),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    retry: false,
    enabled,
  });
}

export const catalogProductKey = (id: string | undefined) => [...CATALOG_QUERY_KEY, 'product', id] as const;

export function useCatalogProduct(id: string | undefined) {
  return useQuery({
    queryKey: catalogProductKey(id),
    queryFn: () => catalogApi.product(id as string),
    enabled: !!id,
    retry: false,
  });
}

/** KAN-754: категории организации плоским списком в порядке дерева (для выбора). */
export function useCatalogCategoryOptions(enabled = true): CategoryOption[] {
  const { data } = useCatalogCategories(enabled);
  return useMemo(() => categoryOptions(buildCategoryTree(data?.categories ?? [])), [data]);
}

/**
 * Обновить весь каталог. skipProductId: карточку удалённого товара не
 * перезапрашивать (иначе 404 до ухода со страницы).
 */
function useInvalidateCatalog() {
  const client = useQueryClient();
  return (skipProductId?: CatalogId) => {
    const skip = skipProductId === undefined ? null : catalogProductKey(String(skipProductId));
    const predicate = (query: Query): boolean =>
      !skip || query.queryKey.length !== skip.length || query.queryKey.some((part, i) => part !== skip[i]);
    return client.invalidateQueries({ queryKey: CATALOG_QUERY_KEY, predicate });
  };
}

export function useCatalogMutations() {
  const invalidate = useInvalidateCatalog();
  const onSuccess = () => invalidate();
  return {
    createCategory: useMutation({
      mutationFn: (body: CatalogCategoryBody) => catalogApi.createCategory(body),
      onSuccess,
    }),
    updateCategory: useMutation({
      mutationFn: (args: { id: CatalogId; body: Partial<CatalogCategoryBody> }) =>
        catalogApi.updateCategory(args.id, args.body),
      onSuccess,
    }),
    deleteCategory: useMutation({
      mutationFn: (id: CatalogId) => catalogApi.deleteCategory(id),
      onSuccess,
    }),
    createProduct: useMutation({
      mutationFn: (body: CatalogProductBody) => catalogApi.createProduct(body),
      onSuccess,
    }),
    updateProduct: useMutation({
      mutationFn: (args: { id: CatalogId; body: Partial<CatalogProductBody> }) =>
        catalogApi.updateProduct(args.id, args.body),
      onSuccess,
    }),
    deleteProduct: useMutation({
      mutationFn: (id: CatalogId) => catalogApi.deleteProduct(id),
      onSuccess: (_data: void, id: CatalogId) => invalidate(id),
    }),
  };
}
